import { create } from "zustand";
import socket from "services/socket";
import { AppLog } from "services";
import { AppRecordJK, StatusMsg } from "utils";

interface SocketState {
  isConnected: boolean;
  joinedChannels: string[];
  appRecord: AppRecordJK;
  joinRoom: (roomId: string) => void;
  leaveRoom: (roomId: string) => void;
  initializeSocket: () => void;
}

const useSocketStore = create<SocketState>((set, get) => {
  const joinedSet = new Set<string>();

  return {
    isConnected: socket.connected ?? false,
    joinedChannels: [],
    appRecord: new AppRecordJK(),

    joinRoom: (roomId: string) => {
      if (!joinedSet.has(roomId)) {
        joinedSet.add(roomId);
        set({ joinedChannels: Array.from(joinedSet) });

        socket.emit("onJoinFlipFlopRoom", roomId, (res: { status: string; message: string }) => {
          if (res.status !== StatusMsg.success.value) {
            AppLog.show({
              message: `❌ Failed to join ${roomId}: ${res.message}`,
              status: StatusMsg.error,
            });
          }
        });
      }
    },

    leaveRoom: (roomId: string) => {
      if (joinedSet.has(roomId)) {
        joinedSet.delete(roomId);
        set({ joinedChannels: Array.from(joinedSet) });

        socket.emit("onLeaveFlipFlopRoom", roomId, (res: { status: string; message: string }) => {
          if (res.status !== StatusMsg.success.value) {
            AppLog.show({
              message: `❌ Failed to leave ${roomId}: ${res.message}`,
              status: StatusMsg.error,
            });
          }
        });
      } else {
        AppLog.show({
          message: `⚠️ Tried to leave non-joined room: ${roomId}`,
          status: StatusMsg.warning,
        });
      }
    },

    initializeSocket: () => {
      socket.on("connect", () => {
        const rooms = Array.from(joinedSet);
        rooms.forEach((roomId) => {
          socket.emit("onJoinFlipFlopRoom", roomId, (res: { status: string; message: string }) => {
            if (res.status === StatusMsg.success.value) {
              AppLog.show({ message: `✅ Rejoined room: ${roomId}` });
            } else {
              AppLog.show({
                message: `❌ Failed to rejoin ${roomId}: ${res.message}`,
                status: StatusMsg.error,
              });
            }
          });
        });

        set({
          isConnected: true,
          joinedChannels: rooms,
        });
      });

      socket.on("disconnect", () => {
        set({ isConnected: false });
      });
    },
  };
});

export default useSocketStore;

